import {
  addHours,
  addMinutes,
  eachDayOfInterval,
  endOfWeek as fnsEndOfWeek,
  format,
  getWeek as fnsGetWeek,
  setWeek as fnsSetWeek,
  getWeekYear as fsGetWeekYear,
  eachWeekOfInterval as fnsEachWeekOfInterval,
  startOfDay,
  startOfWeek as fnsStartOfWeek,
  parseISO,
  setDefaultOptions,
  getHours,
  getMinutes,
  formatISO,
  isToday,
  isYesterday,
  isTomorrow,
  isThisWeek,
  subWeeks,
  addWeeks,
  isEqual,
  isAfter,
  isBefore,
  isSameWeek as _isSameWeek,
} from 'date-fns'
import { fr } from 'date-fns/locale'
import { round, upperFirst } from 'lodash'
import { IsoDate } from '../hooks/use-calendar'

/**
 * Global date defaults
 */
setDefaultOptions({ locale: fr, weekStartsOn: 1 })

export type WeekDay = 0 | 1 | 2 | 3 | 4 | 5 | 6

export const oneMinute = 1000 * 60
export const oneHour = oneMinute * 60
export const oneDay = oneHour * 24

// Formatter commun pour forcer l'affichage sur 2 chiffres sans utiliser padStart
const twoDigits = new Intl.NumberFormat('fr-FR', {
  minimumIntegerDigits: 2,
  useGrouping: false,
})

//Convert date to HTML time value format
export const dateToTimeInputValue = (date?: Date): string => {
  if (!date) return ''
  return new Date(date).toLocaleString('fr-FR', {
    hour: 'numeric',
    minute: 'numeric',
  })
}

/**
 * Get a given date set to a given time
 * @param   {Date}                        date
 * @param   {<input type='time'>.value}   time an HTML time string of type 'HH:MM' in 24h format, "" is valid and means input is empty
 * @return  {undefined}                   if time couldn't be parsed
 * @return  {null}                        if time value is an empty input[type=time] default value : ""
 * @return  {Date}                        the given date, set to the given time
 */
export const timeInputValueToDate = (date: Date, time: string): Date | null | undefined => {
  // The HTML time input field is blank
  if (time === '') return null
  // Matching HH:MM in 24h time format with leading zero : https://stackoverflow.com/a/51177696
  const match = /^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/.exec(time)
  // We return undefined if the provided time was not parseable
  if (!match) return
  // We get the hours and minutes from the string and parse them as numbers
  const [hours, minutes] = time.split(':').map(number => Number(number))
  return addMinutes(addHours(startOfDay(new Date(date)), hours || 0), minutes || 0)
}

/**
 * Transform an hour as number (ex: 5.5) to time input value (ex: 05:30)
 * Limited to 24h format (for HTML time inputs)
 * @param hours {Number}
 * @returns {String}
 */
export const hoursAsNumberToTimeInputValue = (hours: number): string => {
  const hoursAsDate = new Date(hours * oneHour)
  return `${new Date(hoursAsDate).getUTCHours().toString().padStart(2, '0')}:${hoursAsDate
    .getUTCMinutes()
    .toString()
    .padStart(2, '0')}`
}

/**
 * Convert decimal hours to HH:MM format (supports values > 24h)
 * @param hours - Hours as decimal number (e.g., 37.5)
 * @returns Formatted string (e.g., "37:30")
 */
export const formatHoursAsTime = (hours: number): string => {
  if (isNaN(hours) || hours < 0) return '00:00'
  const totalMinutes = Math.floor(hours * 60)
  const h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`
}

/**
 * Transform input type value (ex: 05:30) to hours as number (ex: 5.5)
 * @param inputValue
 * @returns {Number | undefined}
 */

export const timeInputValueToHoursAsNumber = (inputValue: string): number | undefined => {
  const match = /^(0[0-9]|1[0-9]|2[0-3]):([0-5][0-9])$/.exec(inputValue)
  if (!match) return
  return round(Number(match[1]) + Number(match[2]) / 60, 2)
}

// Convert value returned by HTML date input (ex: 2021-12-15) into start and end dates
export const dateInputValuesToWeekDates = (value?: IsoDate): { start: Date; end: Date } => {
  const date = value ? new Date(value) : new Date()
  return {
    start: startOfWeek(date),
    end: endOfWeek(date),
  }
}
export const durationToTimeInputValue = (duration: number): string =>
  `${getHours(duration) - 1}:${getMinutes(duration)}`

export const timeInputValueToDuration = (inputValue: string): number => {
  const match = /([0-9][0-9]):([0-9][0-9])/.exec(inputValue)
  if (!match) throw 'Invalid time input value'
  return Number(match[1]) * 60 * 60 * 1000 + Number(match[2]) * 60 * 1000
}

// Convert date to HTML date input value (ex: 2021-12-15)
export const dateToDateInputValue = (date?: Date): IsoDate => {
  return formatISODate(buildDate(date))
}

// Convert HTML date input value (ex: 2021-12-15) date to date object (UTC midnight)
export const dateInputToDateValue = (date: string): Date => {
  // Split the ISO date string to avoid timezone shifts (e.g. GMT-1 creating previous day)
  const parts = date.split('-')
  if (parts.length < 3) {
    return parseISO(date)
  }
  const [yearPart, monthPart, dayPart] = parts as [string, string, string]
  const year = Number(yearPart)
  const month = Number(monthPart)
  const day = Number(dayPart)
  if ([year, month, day].some(value => Number.isNaN(value))) {
    // Fallback to parseISO to keep previous behavior while signalling bad input
    return parseISO(date)
  }
  // Always build the Date at midnight UTC to match the form date exactly
  return new Date(Date.UTC(year, month - 1, day))
}

/* date-fns wrappers */
type DateParam = Date | string | number
const buildDate = (date?: DateParam): Date => {
  if (!date) return new Date()
  else if (date instanceof Date) return date
  return new Date(date)
}

// Get week number of a date. If no date is passed, the date treated is today
export const getWeek = (date?: DateParam): number => fnsGetWeek(buildDate(date), { locale: fr })

export const getWeekYear = (date: Date): number => fsGetWeekYear(date, { locale: fr })

export const eachWeekOfInterval = (start: Date, end: Date): Date[] =>
  fnsEachWeekOfInterval({ start, end }, { locale: fr, weekStartsOn: 1 })

// Get week number of a date. If no date is passed, the date treated is today
export const setWeek = (date: DateParam, week: number): Date =>
  fnsSetWeek(buildDate(date), week, { locale: fr })

// Get start of week
export const startOfWeek = (date?: DateParam): Date =>
  fnsStartOfWeek(buildDate(date), { weekStartsOn: 1 })

// Get end of week
export const endOfWeek = (date?: DateParam): Date =>
  fnsEndOfWeek(buildDate(date), { weekStartsOn: 1 })

// Corrects the weekday from sunday-based to monday-based
export const getWeekdayMondayBased = (weekdaySundayBased: WeekDay): WeekDay =>
  // If weekDay is 0, we are sunday !
  weekdaySundayBased === 0 ? 6 : ((weekdaySundayBased - 1) as WeekDay)

//Formats

//Ex: mercredi 01 décembre 2021 12:00
export const formatCompleteDateTime = (date?: DateParam): string =>
  date ? format(buildDate(date), "EEEE dd MMMM yyyy H'h'mm", { locale: fr }) : ''

//Ex: mercredi 01 décembre 12:00
export const formatShortDateTime = (date?: DateParam): string =>
  date ? format(buildDate(date), 'dd/MM/yy H:mm:ss', { locale: fr }) : ''

//Ex: mercredi 01 décembre 2021
export const formatCompleteDate = (date?: DateParam): string =>
  date ? format(buildDate(date), 'EEEE dd MMMM yyyy', { locale: fr }) : ''

export const formatTime = (date?: DateParam): string =>
  date ? format(buildDate(date), "HH'h'mm", { locale: fr }) : ''

export const localDate = (date?: DateParam): string =>
  new Date(buildDate(date)).toLocaleDateString()

export const formatDate = (date?: DateParam): string =>
  date ? format(buildDate(date), 'dd/MM/yyyy') : ''

export const formatDateWithTime = (date?: DateParam): string =>
  date ? format(buildDate(date), 'dd/MM/yyyy HH:mm', { locale: fr }) : ''

export const formatDuration = (timestamp: number): string => {
  // Convertir les millisecondes en minutes entières en traitant correctement les valeurs négatives
  // On applique le signe à la fin pour éviter l'arrondi vers -∞ sur les valeurs négatives
  const sign = timestamp < 0 ? '-' : ''
  const totalMinutesAbs = Math.floor(Math.abs(timestamp) / (1000 * 60))
  const hours = Math.floor(totalMinutesAbs / 60)
  const minutes = totalMinutesAbs % 60

  const hoursStr = twoDigits.format(hours)
  const minutesStr = twoDigits.format(minutes)
  return `${sign}${hoursStr}h${minutesStr}`
}

export const formatDay = (date?: DateParam): string =>
  date ? upperFirst(format(buildDate(date), 'E d', { locale: fr })) : ''

/**
 * Long weekday + date label (e.g. "Lun. 12 avril 2026") for tables and lists.
 */
export const formatDayExtended = (date?: DateParam): string =>
  date ? upperFirst(format(buildDate(date), 'EEE d MMMM yyyy', { locale: fr })) : ''

export const formatDayName = (date?: DateParam): string =>
  date ? upperFirst(format(buildDate(date), 'EEEE', { locale: fr })) : ''

//formatInTimeZone(new Date(duration), 'UTC', "HH'h'mm")

export const allDaysOfInterval = (start: DateParam, end: DateParam): Date[] => {
  return eachDayOfInterval({ start: buildDate(start), end: buildDate(end) })
}

export const allDaysOfCurrentWeek = allDaysOfInterval(
  startOfWeek(new Date()),
  endOfWeek(new Date()),
)

//Week days as input options
export const weekDaysAsInputOptions = (days: Date[]): Partial<HTMLOptionElement>[] => [
  { value: '', label: 'Toute la semaine', defaultSelected: true },
  ...days.map(day => ({
    value: day.toString(),
    label: formatDay(day),
  })),
]

export const convertDateToUTCKeepingTime = (date: Date): Date =>
  new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
    ),
  )

export const formatUtcDate = (isoString: string): string => {
  const datePart = isoString.split('T')[0]
  return datePart ? format(parseISO(datePart), 'dd/MM/yyyy', { locale: fr }) : ''
}

/**
 * @param {Date} date a Date object
 * @returns {string} a string of the format YYYY-MM-DD compliant with ISO8601
 */
export const formatISODate = (date: Date): IsoDate => {
  return formatISO(date, { representation: 'date' }) as IsoDate
}

export const dateRangeDisplayHelper = (start: IsoDate, end: IsoDate, contextual = true): string => {
  const startDate = parseISO(start)
  const endDate = parseISO(end)

  try {
    const dateRangeIsDay = eachDayOfInterval({ start: startDate, end: endDate }).length === 1

    if (contextual) {
      const dataRangeTitle = dateRangeIsDay
        ? isToday(startDate)
          ? "Aujourd'hui"
          : isYesterday(startDate)
            ? 'Hier'
            : isTomorrow(startDate)
              ? 'Demain'
              : formatCompleteDate(startDate)
        : isThisWeek(startDate) && isThisWeek(endDate)
          ? 'Cette Semaine'
          : isThisWeek(subWeeks(startDate, 1)) && isThisWeek(subWeeks(endDate, 1))
            ? 'Semaine Prochaine'
            : isThisWeek(addWeeks(startDate, 1)) && isThisWeek(addWeeks(endDate, 1))
              ? 'Semaine Passée'
              : `Du ${formatCompleteDate(startDate)} au ${formatCompleteDate(endDate)}`

      return dataRangeTitle
    } else {
      const dateIs = dateRangeIsDay
        ? formatCompleteDate(start)
        : `Du ${formatCompleteDate(startDate)} au ${formatCompleteDate(endDate)}`
      return dateIs
    }
  } catch {
    // Bad date range interval
    return 'Intervalle de dates invalide'
  }
}

/**
 * Converts a timestamp duration to decimal hours
 * @example 61200000 (17h00) => 17
 * @example 61500000 (17h05) => 17.08
 * @param timestamp - Duration in milliseconds
 * @returns number - Hours in decimal format with 2 decimal places
 */
export const formatDurationAsDecimal = (timestamp: number): string => {
  // Gestion correcte des durées négatives en séparant le signe de la valeur absolue
  const sign = timestamp < 0 ? -1 : 1
  const totalMinutesAbs = Math.floor(Math.abs(timestamp) / (1000 * 60))
  const hours = Math.floor(totalMinutesAbs / 60)
  const minutes = totalMinutesAbs % 60

  const value = sign * (hours + minutes / 60)
  return Number(value.toFixed(2)).toString()
}

/**
 * Vérifie si une heure donnée est comprise dans une plage horaire
 * Gère le cas où la plage s'étend sur plusieurs jours (ex: 18h à 5h le lendemain)
 * @param inputDate - La date/heure à vérifier
 * @param startDate - Date de début de la plage
 * @param endDate - Date de fin de la plage
 * @returns true si l'heure est dans la plage, false sinon
 */
export const isTimeInRange = (inputDate: Date, startDate: Date, endDate: Date): boolean => {
  const inputTime = inputDate.getHours() * 60 + inputDate.getMinutes()
  const startTime = startDate.getHours() * 60 + startDate.getMinutes()
  const endTime = endDate.getHours() * 60 + endDate.getMinutes()

  // Si la plage s'étend sur plusieurs jours (ex: 18h à 5h le lendemain)
  if (startTime > endTime) {
    // L'heure doit être >= startTime OU <= endTime
    return inputTime >= startTime || inputTime <= endTime
  } else {
    // Plage normale dans la même journée
    return inputTime >= startTime && inputTime <= endTime
  }
}

export const isDateInRange = (date: Date, startDate: Date, endDate: Date): boolean => {
  date = buildDate(date)
  startDate = buildDate(startDate)
  endDate = buildDate(endDate)
  return (
    isEqual(date, startDate) ||
    isEqual(date, endDate) ||
    (isAfter(date, startDate) && isBefore(date, endDate))
  )
}

/**
 * Helper function to convert minutes to a human-readable format (hours and minutes)
 * @param minutes - The number of minutes to convert
 * @returns A string representation like "2 heures" or "2 heures 30 minutes"
 */
export const formatReadableMinutesToHoursAndMinutes = (minutes: number): string => {
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60

  if (hours === 0) {
    return `${remainingMinutes} minute${remainingMinutes > 1 ? 's' : ''}`
  }

  if (remainingMinutes === 0) {
    return `${hours} heure${hours > 1 ? 's' : ''}`
  }

  return `${hours} heure${hours > 1 ? 's' : ''} ${remainingMinutes} minute${remainingMinutes > 1 ? 's' : ''}`
}

export const isSameWeek = (date1: Date, date2: Date): boolean => {
  return _isSameWeek(date1, date2, { locale: fr, weekStartsOn: 1 })
}
